import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseAssessmentAdapter } from './BaseAssessmentAdapter'
import { AdapterOptions, SectionAdapter } from './types'
import { BaseSubsectionAdapter } from './BaseSubsectionAdapter'
import { SupervisoryFormData } from '../SupervisoryAssessment/types/SupervisoryFormData'
import { AssessmentType } from '../../Clinical/Assessments/constants'
import { mutateFlatAnswers } from '../helpers'
import { AssessmentOverviewAdapter } from '../SupervisoryAssessment/sections/assessment-overview/adapter'
import { PatientOverviewAdapter } from '../SupervisoryAssessment/sections/patient-overview/adapter'
import { SupervisoryReviewAdapter } from '../SupervisoryAssessment/sections/supervisory-review/adapter'
import { FinalStepsAdapter } from '../SupervisoryAssessment/sections/final-steps/adapter'

/**
 * Array of all supervisory section adapter instances
 */
const SUPERVISORY_SECTION_ADAPTERS:
  | BaseSubsectionAdapter<SupervisoryFormData>[]
  | SectionAdapter<SupervisoryFormData>[] = [
  new AssessmentOverviewAdapter(),
  new PatientOverviewAdapter(),
  new SupervisoryReviewAdapter(),
  new FinalStepsAdapter()
]

export class SupervisoryAssessmentAdapter extends BaseAssessmentAdapter<
  SupervisoryFormData & Record<string, unknown>
> {
  readonly assessmentType = AssessmentType.SUPERVISORY

  protected flatAnswersToFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): SupervisoryFormData & Record<string, unknown> {
    // Use composable section adapters for clean, maintainable extraction
    const formData: Partial<SupervisoryFormData> = {}

    // Extract data from all section adapters dynamically
    for (const adapter of SUPERVISORY_SECTION_ADAPTERS) {
      const sectionData = adapter.extractFormData(flatAnswers, options)
      Object.assign(formData, sectionData)
    }

    return formData as SupervisoryFormData & Record<string, unknown>
  }

  protected formDataToFlatAnswers(
    formData: SupervisoryFormData,
    currentFlatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Record<string, QuestionnaireResponseItem> {
    // Use composable section adapters for clean, maintainable transformation
    const allMutations: Array<{ linkId: string; value: unknown }> = []

    // Build mutations from all section adapters dynamically
    for (const adapter of SUPERVISORY_SECTION_ADAPTERS) {
      if (adapter instanceof BaseSubsectionAdapter) {
        adapter.setFormData(formData)
        allMutations.push(...adapter.buildFlatAnswers())
      } else if (adapter instanceof SectionAdapter) {
        allMutations.push(...adapter.buildFlatAnswers(formData))
      }
    }

    // Apply all mutations using existing helper
    mutateFlatAnswers(currentFlatAnswers, allMutations)

    return currentFlatAnswers
  }
}
